import React from 'react';
import { Input } from '@/components/ui/input';
import {
  AddressVisibility,
  TourTarget,
  TourVisibility,
  useArchiveVirtualTourMutation,
  useLinkVirtualTourMutation,
  useMoveVirtualTourMutation,
  useReactivateVirtualTourMutation,
  useRenameVirtualTourMutation,
  useSetAddressVisibilityMutation,
  useSetVisibilityVirtualTourMutation,
  useUnlinkVirtualTourMutation,
} from '@/client/api/listing-management/virtual-tours.mutation';
import { useVirtualTourInventoryQuery } from '@/client/api/listing-management/virtual-tours.query';
import { ActionDialog } from './ActionDialog';
import { ListingPicker } from './ListingPicker';

type Closable = { open: boolean; onClose: () => void };

export const ArchiveDialog: React.FC<Closable & { target: TourTarget }> = ({ open, onClose, target }) => {
  const m = useArchiveVirtualTourMutation();
  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Archive this tour?"
      description="Frees the Matterport space. This is recorded in the admin audit."
      confirmLabel="Archive"
      forceLabel="Archive anyway"
      successMessage="Tour archived."
      submit={(reason, f) => m.mutateAsync({ ...target, reason, ...f })}
    />
  );
};

export const ReactivateDialog: React.FC<Closable & { target: TourTarget }> = ({ open, onClose, target }) => {
  const m = useReactivateVirtualTourMutation();
  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Reactivate this tour?"
      description="Takes a Matterport space (a free reactivation is used when available)."
      confirmLabel="Reactivate"
      successMessage="Tour reactivated."
      submit={(reason) => m.mutateAsync({ ...target, reason })}
    />
  );
};

export const UnlinkDialog: React.FC<Closable & { vtid: number }> = ({ open, onClose, vtid }) => {
  const m = useUnlinkVirtualTourMutation();
  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Unlink this tour?"
      description="Detaches the tour from its listing. The Matterport model is not deleted."
      confirmLabel="Unlink"
      successMessage="Tour unlinked."
      submit={(reason) => m.mutateAsync({ vtid, reason })}
    />
  );
};

export const MoveDialog: React.FC<Closable & { vtid: number }> = ({ open, onClose, vtid }) => {
  const m = useMoveVirtualTourMutation();
  const [eid, setEid] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (open) setEid(null);
  }, [open]);
  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Move this tour to another listing?"
      description="Re-links the model to the listing you pick."
      confirmLabel="Move"
      successMessage="Tour moved."
      allowBypass
      valid={eid != null}
      submit={(reason, f) => m.mutateAsync({ vtid, eid: eid as number, reason, bypass_quota: f.bypass_quota })}
    >
      <ListingPicker eid={eid} onChange={setEid} />
    </ActionDialog>
  );
};

/**
 * Link a Matterport model to a listing. Pass `modelId` to fix the model (inventory),
 * `eid` to fix the listing (listing detail page); the missing side is picked/typed.
 */
export const LinkDialog: React.FC<Closable & { modelId?: string; eid?: number }> = ({ open, onClose, modelId, eid: fixedEid }) => {
  const m = useLinkVirtualTourMutation();
  const [eid, setEid] = React.useState<number | null>(null);
  const [model, setModel] = React.useState('');
  React.useEffect(() => {
    if (open) {
      setEid(null);
      setModel('');
    }
  }, [open]);
  const inventory = useVirtualTourInventoryQuery({ link: 'unlinked' });
  const modelValue = modelId ?? model.trim();
  const eidValue = fixedEid ?? eid;
  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Link a Matterport model"
      description="Attaches the model to a listing as its virtual tour."
      confirmLabel="Link"
      successMessage="Model linked."
      allowBypass
      valid={!!modelValue && eidValue != null}
      submit={(reason, f) =>
        m.mutateAsync({ model_id: modelValue, eid: eidValue as number, reason, bypass_quota: f.bypass_quota })
      }
    >
      {modelId == null && (
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Matterport model id</span>
          <Input placeholder="e.g. SxQL3iGyoDo" value={model} onChange={(e) => setModel(e.target.value)} />
          <select
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value=""
            onChange={(e) => e.target.value && setModel(e.target.value)}
          >
            <option value="">Or pick from unlinked inventory…</option>
            {(inventory.data?.models ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name || x.id} ({x.id})
              </option>
            ))}
          </select>
        </label>
      )}
      {fixedEid == null && <ListingPicker eid={eid} onChange={setEid} />}
    </ActionDialog>
  );
};

export const VisibilityDialog: React.FC<Closable & { target: TourTarget; current?: string | null }> = ({
  open,
  onClose,
  target,
  current,
}) => {
  const m = useSetVisibilityVirtualTourMutation();
  const [value, setValue] = React.useState<TourVisibility>('private');
  React.useEffect(() => {
    if (open) setValue(current === 'public' || current === 'unlisted' ? current : 'private');
  }, [open, current]);
  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Change tour visibility"
      description="Controls who can open the tour on Matterport."
      confirmLabel="Apply"
      forceLabel="Make private anyway"
      successMessage="Visibility updated."
      submit={(reason, f) => m.mutateAsync({ ...target, visibility: value, reason, ...f })}
    >
      <label className="block space-y-1.5">
        <span className="text-sm font-medium">Visibility</span>
        <select
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={value}
          onChange={(e) => setValue(e.target.value as TourVisibility)}
        >
          <option value="private">Private</option>
          <option value="unlisted">Unlisted</option>
          <option value="public">Public</option>
        </select>
      </label>
    </ActionDialog>
  );
};

export const AddressVisibilityDialog: React.FC<Closable & { vtid: number }> = ({ open, onClose, vtid }) => {
  const m = useSetAddressVisibilityMutation();
  const [value, setValue] = React.useState<AddressVisibility>('partial');
  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Change address visibility"
      description="Controls how much of the address Matterport shows."
      confirmLabel="Apply"
      successMessage="Address visibility updated."
      submit={(reason) => m.mutateAsync({ vtid, visibility: value, reason })}
    >
      <label className="block space-y-1.5">
        <span className="text-sm font-medium">Address</span>
        <select
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={value}
          onChange={(e) => setValue(e.target.value as AddressVisibility)}
        >
          <option value="full">Full</option>
          <option value="partial">Partial</option>
          <option value="none">None</option>
        </select>
      </label>
    </ActionDialog>
  );
};

export const RenameDialog: React.FC<Closable & { vtid: number; name?: string | null }> = ({ open, onClose, vtid, name }) => {
  const m = useRenameVirtualTourMutation();
  const [n, setN] = React.useState('');
  const [d, setD] = React.useState('');
  React.useEffect(() => {
    if (open) {
      setN(name ?? '');
      setD('');
    }
  }, [open, name]);
  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title="Rename tour"
      description="Updates the model name and/or description on Matterport. Leave the description blank to keep it."
      confirmLabel="Save"
      successMessage="Tour details updated."
      valid={!!n.trim() || !!d.trim()}
      submit={(reason) => m.mutateAsync({ vtid, reason, name: n.trim() || undefined, description: d.trim() || undefined })}
    >
      <label className="block space-y-1.5">
        <span className="text-sm font-medium">Name</span>
        <Input value={n} onChange={(e) => setN(e.target.value)} />
      </label>
      <label className="block space-y-1.5">
        <span className="text-sm font-medium">Description</span>
        <Input value={d} onChange={(e) => setD(e.target.value)} />
      </label>
    </ActionDialog>
  );
};
